import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ScrollToTop from "@/components/ScrollToTop";
import CookieBanner from "@/components/CookieBanner";
import Index from "./pages/Index";

const BrandPage = lazy(() => import("./pages/BrandPage"));
const VisionPage = lazy(() => import("./pages/VisionPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ExplorerPage = lazy(() => import("./pages/ExplorerPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const Top10Page = lazy(() => import("./pages/Top10Page"));
const Top10HubPage = lazy(() => import("./pages/Top10HubPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const AlternativesPage = lazy(() => import("./pages/AlternativesPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation}>
        <BrowserRouter>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <CookieBanner />
              <Suspense fallback={<div className="min-h-screen bg-background" />}>
                <Routes>
                  {/* English (default) routes */}
                  <Route path="/" element={<Index />} />
                  <Route path="/vision" element={<VisionPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/explorer" element={<ExplorerPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/brand/:id" element={<BrandPage />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/top-10" element={<Top10HubPage />} />
                  <Route path="/top-10/:slug" element={<Top10Page />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/compare/:slug" element={<ComparePage />} />
                  <Route path="/alternatives/:slug" element={<AlternativesPage />} />

                   {/* French /fr/ prefix routes */}
                  <Route path="/fr" element={<Index />} />
                  <Route path="/fr/vision" element={<VisionPage />} />
                  <Route path="/fr/faq" element={<FAQPage />} />
                  <Route path="/fr/explorer" element={<ExplorerPage />} />
                  <Route path="/fr/blog" element={<BlogPage />} />
                  <Route path="/fr/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/fr/brand/:id" element={<BrandPage />} />
                  <Route path="/fr/legal" element={<LegalPage />} />
                  <Route path="/fr/privacy" element={<PrivacyPage />} />
                  <Route path="/fr/terms" element={<TermsPage />} />
                  <Route path="/fr/top-10" element={<Top10HubPage />} />
                  <Route path="/fr/top-10/:slug" element={<Top10Page />} />
                  <Route path="/fr/compare" element={<ComparePage />} />
                  <Route path="/fr/compare/:slug" element={<ComparePage />} />
                  <Route path="/fr/alternatives/:slug" element={<AlternativesPage />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TooltipProvider>
          </LanguageProvider>
        </BrowserRouter>
      </LazyMotion>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
